/* eslint-disable unicorn/no-null -- Model picker emits `null` placeholders for optional stats in the UI. */

import type { AIModelsResponse } from '@sweetistics/contracts';
import {
  canonicalizeModelId,
  getModelDisplayName,
  getModelIdForRole,
  getModelOptions,
  getModelRuntimeDisplayName,
  getProviderFromModel,
  MODEL_REGISTRY_BY_ID,
  MODEL_ROLES,
  type ModelCapability,
  type ModelDefinition,
  type ModelProvider,
  type ModelTier,
} from '@/constants/ai';
import { getModelCapabilityProfile } from '@/lib/ai/model-capabilities';
import type { ModelPickerOption } from '@/types/ai';

const EMPTY_CAPABILITIES: readonly ModelCapability[] = Object.freeze<ModelCapability[]>([]);

type RawModelOption = ReturnType<typeof getModelOptions>[number];

type ProviderSafe = ModelProvider | 'custom';

function resolveProvider(base: RawModelOption | undefined, canonicalId: string): ProviderSafe {
  if (base?.provider) {
    return base.provider;
  }
  const registryProvider = MODEL_REGISTRY_BY_ID[canonicalId]?.provider;
  if (registryProvider) {
    return registryProvider;
  }
  const fallback = getProviderFromModel(canonicalId).trim().toLowerCase();
  if (
    fallback === 'openai' ||
    fallback === 'anthropic' ||
    fallback === 'google' ||
    fallback === 'meta' ||
    fallback === 'xai' ||
    fallback === 'mistral'
  ) {
    return fallback;
  }
  return 'custom';
}

function resolveTier(base: RawModelOption | undefined, canonicalId: string): ModelTier {
  if (base?.tier) {
    return base.tier;
  }
  const registryTier = MODEL_REGISTRY_BY_ID[canonicalId]?.tier;
  if (registryTier) {
    return registryTier;
  }
  return 'standard';
}

function resolveCapabilities(base: RawModelOption | undefined, canonicalId: string): readonly ModelCapability[] {
  if (base?.capabilities) {
    return base.capabilities;
  }
  const registryCapabilities = MODEL_REGISTRY_BY_ID[canonicalId]?.capabilities;
  if (registryCapabilities) {
    return registryCapabilities;
  }
  return EMPTY_CAPABILITIES;
}

function resolveDefaultConfig(
  base: RawModelOption | undefined,
  canonicalId: string
): ModelDefinition['defaultConfig'] | null {
  if (base?.defaultConfig) {
    return base.defaultConfig;
  }
  const registryConfig = MODEL_REGISTRY_BY_ID[canonicalId]?.defaultConfig;
  return registryConfig ?? null;
}

function resolveDescription(base: RawModelOption | undefined, canonicalId: string): string | null {
  if (typeof base?.description === 'string') {
    return base.description;
  }
  const registryDescription = MODEL_REGISTRY_BY_ID[canonicalId]?.description;
  return registryDescription ?? null;
}

function resolveLabel(base: RawModelOption | undefined, canonicalId: string): string {
  if (base?.label) {
    return base.label;
  }
  const registryLabel = MODEL_REGISTRY_BY_ID[canonicalId]?.label;
  if (registryLabel) {
    return registryLabel;
  }
  return getModelDisplayName(canonicalId);
}

export function toModelPickerOption(candidate: string): ModelPickerOption | null {
  const canonicalId = canonicalizeModelId(candidate) ?? candidate;
  const base = getModelOptions().find((option) => option.value === canonicalId);
  if (!(base || MODEL_REGISTRY_BY_ID[canonicalId])) {
    return null;
  }
  return fromModelDefinition(canonicalId, base);
}

function fromModelDefinition(canonicalId: string, base?: RawModelOption): ModelPickerOption {
  const registryEntry = MODEL_REGISTRY_BY_ID[canonicalId];
  const providerModelId = registryEntry?.providerModelId?.trim() || canonicalId;
  const capabilityProfile = getModelCapabilityProfile(canonicalId);

  return {
    id: providerModelId,
    canonicalId,
    providerModelId,
    displayName: getModelRuntimeDisplayName(providerModelId),
    label: resolveLabel(base, canonicalId),
    description: resolveDescription(base, canonicalId),
    provider: resolveProvider(base, canonicalId),
    tier: resolveTier(base, canonicalId),
    capabilities: resolveCapabilities(base, canonicalId),
    contextWindowTokens: capabilityProfile.totalContextTokens,
    tweetLimit: capabilityProfile.tweetAnalysisLimit,
    defaultConfig: resolveDefaultConfig(base, canonicalId),
    defaultEnabled: Boolean(base?.defaultEnabled ?? registryEntry?.defaultEnabled ?? false),
    config: null,
  } satisfies ModelPickerOption;
}

const REASONING_CAPABILITY = 'reasoning';
const DEFAULT_ROLE_PRIORITY = [MODEL_ROLES.PREMIUM, MODEL_ROLES.STANDARD] as const;

const KNOWN_PROVIDERS = new Set<string>(['openai', 'anthropic', 'google', 'meta', 'xai', 'mistral', 'custom']);
const KNOWN_TIERS = new Set<string>(['premium', 'standard', 'experimental']);

export type ContractModelOption = AIModelsResponse['models'][number];

const normalizeModelConfig = (
  config: ContractModelOption['config'] | ModelPickerOption['config'] | null | undefined
): ModelPickerOption['config'] => {
  if (!config) {
    return null;
  }

  const normalized: NonNullable<ModelPickerOption['config']> = {};

  if (typeof config.maxTokens === 'number') {
    normalized.maxTokens = config.maxTokens;
  }

  if (typeof config.temperature === 'number') {
    normalized.temperature = config.temperature;
  }

  if (config.providerOptions) {
    normalized.providerOptions = config.providerOptions;
  }

  return normalized;
};

export function fromContractModelOption(option: ContractModelOption): ModelPickerOption {
  const base = toModelPickerOption(option.canonicalId) ?? {
    id: option.id,
    canonicalId: option.canonicalId,
    providerModelId: option.providerModelId,
    displayName: option.displayName,
    label: option.label || option.displayName,
    description: option.description || null,
    provider: 'custom',
    tier: 'standard',
    capabilities: EMPTY_CAPABILITIES,
    contextWindowTokens: null,
    tweetLimit: null,
    defaultConfig: null,
    defaultEnabled: false,
    config: null,
  };

  const providerCandidate = option.provider.toLowerCase();
  const provider = isProviderValue(providerCandidate) ? providerCandidate : base.provider;

  const tierCandidate = option.tier.toLowerCase();
  const tier = isModelTierValue(tierCandidate) ? tierCandidate : base.tier;

  const description = option.description.trim().length > 0 ? option.description : base.description;
  const label = option.label.trim().length > 0 ? option.label : base.label;

  return {
    ...base,
    id: option.id,
    canonicalId: option.canonicalId,
    providerModelId: option.providerModelId,
    displayName: option.displayName,
    label,
    description,
    provider,
    tier,
    config: normalizeModelConfig(option.config ?? base.config),
  };
}

const isProviderValue = (value: string): value is ModelPickerOption['provider'] => KNOWN_PROVIDERS.has(value);

const isModelTierValue = (value: string): value is ModelTier => KNOWN_TIERS.has(value);

export function resolveDefaultModelId(models: ModelPickerOption[]): string | null {
  if (!Array.isArray(models) || models.length === 0) {
    return null;
  }

  for (const role of DEFAULT_ROLE_PRIORITY) {
    const canonicalModelId = getModelIdForRole(role);
    if (canonicalModelId === undefined) {
      continue;
    }
    const pickerOption = toModelPickerOption(canonicalModelId);
    if (!pickerOption) {
      continue;
    }

    const matched = models.find(
      (model) => model.canonicalId === pickerOption.canonicalId || model.id === pickerOption.providerModelId
    );
    if (matched) {
      return matched.id;
    }
  }

  const reasoningCapable = models.find((model) => model.capabilities.includes(REASONING_CAPABILITY));
  if (reasoningCapable) {
    return reasoningCapable.id;
  }

  return models[0]?.id ?? null;
}

export function buildModelPickerOptions(filter?: (option: ModelPickerOption) => boolean): ModelPickerOption[] {
  const catalog = getModelOptions();
  const options = catalog.map((option) => fromModelDefinition(option.value, option));
  if (typeof filter === 'function') {
    return options.filter((option) => filter(option));
  }
  return options;
}
